// HOY — la razón por la que el dueño abre el Monitor.
//
// El principio de esta pantalla: **la frase es la respuesta, el número es la
// prueba**. Un dashboard que muestra "$118.000" obliga a que el dueño se acuerde
// de cuánto hace un jueves normal para saber si eso está bien. Éste se lo dice.
//
// Por eso el bloque usa la anatomía común (Bloque.js) y no un encabezado propio:
// el TITULAR es la conclusión —"Cerraste 14% arriba de un jueves normal"— y el
// número grande quedó DEBAJO, como la prueba de esa conclusión. Antes el número
// iba primero y la frase después, que es el orden inverso al que se lee.
//
// Tres decisiones que hacen que la frase sea confiable:
//
// 1. Se compara contra el MISMO DÍA DE LA SEMANA. Un martes contra un domingo no
//    dice nada.
// 2. NO se emite veredicto hasta que el día terminó. Antes se estimaba cuánto
//    del día iba transcurrido con la curva de OCUPACIÓN y se compara contra eso.
//    Estaba mal: la plata entra cuando el auto SALE, no cuando entra. A las 10 de
//    la mañana tenés medio estacionamiento lleno y casi nada cobrado, así que el
//    cálculo gritaba "100% abajo" TODAS las mañanas de TODOS los clientes.
//    Mientras el día corre se informa AVANCE ("llevas X de los Y que suele dejar
//    un jueves"), que no es un juicio y no puede ser un falso positivo. El
//    veredicto —arriba/abajo, con color— recién aparece con el día cerrado, que es
//    cuando comparar es justo.
// 3. Se usa la MEDIANA, no el promedio. Un solo día excepcional —un evento, una
//    lluvia— corre el promedio y deja la vara donde no corresponde para el resto
//    del mes.
//
// Y los días cerrados quedan afuera: un domingo en cero no describe cómo es un
// domingo normal, describe que ese día no se abrió.

import React, { useMemo } from "react";
import { Row, Col } from "reactstrap";
import Bloque from "./Bloque";
import { ORIGEN, UI, POLARIDAD } from "./theme";
import { formatCurrency, horas, entero, fecha } from "./format";
import { patronSemanal, referenciaDelDia, isoLocal } from "./baseline";

const DIAS = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];
const diaDeSemana = (iso) => new Date(`${iso}T00:00:00`).getDay();
const nombreDia = (iso) => (iso ? DIAS[diaDeSemana(iso)] : "día");

/** "sábado" → "sábados"; "jueves" → "jueves". Sin esto sale "juevess". */
const plural = (d) => (d.endsWith("s") ? d : `${d}s`);

function mediana(nums) {
  if (!nums.length) return null;
  const o = [...nums].sort((a, b) => a - b);
  const m = Math.floor(o.length / 2);
  return o.length % 2 ? o[m] : (o[m - 1] + o[m]) / 2;
}

/**
 * ¿El documento `live` es de HOY?
 *
 * `live` se pisa a sí mismo y NO se borra: si la PC del estacionamiento deja de
 * enviar, ahí queda para siempre lo último que mandó. Su `ts` es la única forma
 * de saber de cuándo es.
 *
 * Sin esta comprobación pasaba esto, verificado con un cliente real cuyos datos
 * terminan el 24/07: la pantalla decía "Llevas $527.200" el 27/08. Eran los
 * ingresos de hace CINCO SEMANAS presentados como los de hoy, y el bloque ya
 * sabía que no tenía el día —escribía "un día" en vez de "un jueves"— pero
 * mostraba el número igual.
 *
 * Ante la duda se descarta: un cero honesto es infinitamente mejor que plata
 * vieja con cara de actual, sobre todo en la primera cifra de la pantalla.
 */
function vivoEsDeHoy(vivo, hoyISO) {
  const ts = vivo?.ts;
  if (!ts) return false;
  const s = String(ts);
  // El Desktop lo manda como "YYYY-MM-DDTHH:MM:SS", hora local de esa PC.
  return s.slice(0, 10) === hoyISO;
}

const PulsoDeHoy = ({ hoy, cerrados, vivo, capacidad }) => {
  const c = useMemo(() => {
    const hoyISO = isoLocal(new Date());
    // El respaldo del documento `live` SÓLO vale si es de hoy.
    const vivoHoy = vivoEsDeHoy(vivo, hoyISO) ? vivo?.today : null;

    const ingresoHoy = hoy?.income ?? vivoHoy?.income ?? 0;
    const entradasHoy = hoy?.tickets?.total?.in ?? vivoHoy?.tickets_in ?? 0;
    const salidasHoy = hoy?.tickets?.total?.out ?? vivoHoy?.tickets_out ?? 0;

    const dow = hoy?.date ? diaDeSemana(hoy.date) : new Date().getDay();

    // `mismos` se sigue usando SOLO para las curvas de ocupación de abajo, que
    // necesitan los días concretos y no una mediana.
    const mismos = cerrados
      .filter((d) => diaDeSemana(d.date) === dow)
      .filter((d) => (d.income || 0) > 0 || (d.tickets?.total?.in || 0) > 0)
      .slice(-8);

    // LA REFERENCIA SALE DEL MÓDULO COMPARTIDO, no de una mediana propia.
    //
    // Este bloque calculaba la suya sobre los últimos 8 jueves, y "Tus días" y
    // el resumen la calculan sobre 90 días. Dos números distintos para la misma
    // frase —"lo que suele dejarte un jueves"— en la misma pantalla, con una
    // semana de diferencia entre uno y otro. Ese es exactamente el problema que
    // esta pantalla existe para eliminar, y no se arregla revisando: se arregla
    // haciendo que sea imposible, o sea compartiendo el cálculo.
    const patron = patronSemanal(cerrados);
    const referencia = referenciaDelDia(patron, hoy?.date || isoLocal(new Date()));

    // Qué porción de la ACTIVIDAD del día ya ocurrió, según la curva de ocupación
    // de esos mismos días. Sirve sólo para saber si el día YA TERMINÓ —no para
    // estimar cuánta plata debería haber entrado, ver la nota de arriba—.
    const h = new Date().getHours();
    const curvas = mismos
      .map((d) => d?.occupancy?.hourly)
      .filter((x) => Array.isArray(x) && x.length === 24);
    let avance = null;
    if (curvas.length) {
      const total = curvas.reduce((s, x) => s + x.reduce((a, b) => a + (b || 0), 0), 0);
      const hasta = curvas.reduce((s, x) => s + x.slice(0, h + 1).reduce((a, b) => a + (b || 0), 0), 0);
      if (total > 0) avance = hasta / total;
    }

    // Sin curva no se sabe si el día terminó; se asume que no y se informa avance,
    // que nunca miente. Mejor callar un veredicto que emitir uno falso.
    const diaTerminado = avance !== null && avance >= 0.92;

    // El veredicto compara día COMPLETO contra día COMPLETO. Es la única
    // comparación justa que estos datos permiten.
    const pct = diaTerminado && referencia !== null && referencia > 0
      ? Math.round(((ingresoHoy - referencia) / referencia) * 100)
      : null;

    const permanencias = cerrados
      .filter((d) => (d.stay?.avg_hours || 0) > 0)
      .slice(-14)
      .map((d) => d.stay.avg_hours);

    return {
      ingresoHoy, entradasHoy, salidasHoy,
      referencia, pct, diaTerminado,
      // Cuantos dias de ESE dia de la semana midio la vara compartida. Antes
      // era `mismos.length` (los ultimos 8) y quedo mintiendo cuando la
      // referencia paso a salir de baseline.js, que mira 90 dias.
      nMuestras: patron.base[dow]?.n || 0,
      sinMovimiento: ingresoHoy === 0 && entradasHoy === 0 && salidasHoy === 0,
      permanencia: permanencias.length ? mediana(permanencias) : null,
      // Mismo criterio: "adentro ahora" de un live viejo no es ahora.
      adentro: vivoEsDeHoy(vivo, isoLocal(new Date())) ? (vivo?.occupancy?.current ?? null) : null,
    };
  }, [hoy, cerrados, vivo]);

  const dia = nombreDia(hoy?.date);
  const pctOcup = capacidad > 0 && c.adentro !== null
    ? Math.round((c.adentro / capacidad) * 100) : null;
  const refTexto = c.referencia !== null ? formatCurrency(c.referencia) : null;

  // ── El titular: la conclusión. El número va debajo, como prueba. ──────
  let titular;
  let frase;
  let veredicto = null;   // el porcentaje con color, pegado al número

  if (c.nMuestras < 4) {
    titular = `Todavía no puedo decirte si este ${dia} es bueno o malo`;
    frase = `Para comparar necesito al menos cuatro ${plural(dia)} con movimiento y llevo ${c.nMuestras}. Se completa solo: cada jornada que TicketControl cierra suma una, y en un mes ya vas a tener el dato.`;
  } else if (!c.diaTerminado) {
    // Día en curso: se informa AVANCE, nunca veredicto. Un cero a las 8 de la
    // mañana es lo normal, no una mala noticia.
    titular = c.sinMovimiento
      ? "Todavía no hubo movimiento hoy"
      : `Llevas ${formatCurrency(c.ingresoHoy)} de los ${refTexto} que suele dejarte un ${dia}`;
    frase = c.sinMovimiento
      ? `Un ${dia} normal te deja ${refTexto}. Es temprano para sacar conclusiones: la plata entra cuando el vehículo sale, no cuando entra.`
      : "Es el avance del día, no un veredicto: la plata entra cuando el vehículo sale, así que a la mañana el día siempre va atrás. La comparación justa aparece cuando la jornada cierra.";
  } else if (Math.abs(c.pct) <= 8) {
    titular = `Cerraste un ${dia} normal`;
    frase = `Un ${dia} habitual te deja ${refTexto} y hoy quedaste a menos del 8% de eso. No hay nada para revisar.`;
    veredicto = { texto: "dentro de lo normal", color: UI.textoSuave };
  } else if (c.pct > 0) {
    titular = `Cerraste ${c.pct}% arriba de un ${dia} normal`;
    frase = `Un ${dia} habitual te deja ${refTexto}. Si sabes qué pasó hoy —un evento cerca, una promoción, una tarifa nueva— conviene anotarlo: es lo único que después se puede repetir a propósito.`;
    veredicto = { texto: `+${c.pct}% contra un ${dia} normal`, color: POLARIDAD.entra.color };
  } else {
    titular = `Cerraste ${Math.abs(c.pct)}% abajo de un ${dia} normal`;
    frase = `Un ${dia} habitual te deja ${refTexto}. Un día flojo suelto no es una tendencia; si se repite en el mismo día de la semana, ahí sí vale la pena mirarlo de cerca.`;
    veredicto = { texto: `−${Math.abs(c.pct)}% contra un ${dia} normal`, color: POLARIDAD.sale.color };
  }

  // El rango real, no "hoy" a secas: contra cuántos días equivalentes se compara.
  const fechaHoy = hoy?.date ? fecha(hoy.date) : fecha(new Date());
  const rango = c.nMuestras > 0
    ? `${dia} ${fechaHoy} · comparado contra tus ${c.nMuestras} ${plural(dia)} con movimiento de los últimos 90 días`
    : `${dia} ${fechaHoy} · todavía no hay ${plural(dia)} anteriores con movimiento para comparar`;

  return (
    <Bloque
      nombre="Cómo va hoy"
      id="pulso-de-hoy"
      titular={titular}
      frase={frase}
      rango={rango}
      acento={ORIGEN.tickets.color}
    >
      {/* La prueba del titular: lo que lleva cobrado el día. */}
      <div className="d-flex align-items-baseline flex-wrap" style={{ gap: 10 }}>
        <div>
          <div style={{ fontSize: 11.5, color: UI.textoSuave, textTransform: "uppercase", letterSpacing: 0.5 }}>
            Cobrado hoy
          </div>
          <div style={{ fontSize: 34, fontWeight: 800, color: UI.texto, lineHeight: 1.15, marginTop: 2 }}>
            {formatCurrency(c.ingresoHoy)}
          </div>
        </div>
        {veredicto && (
          <div style={{ fontSize: 14, fontWeight: 700, color: veredicto.color, paddingBottom: 4 }}>
            {veredicto.texto}
          </div>
        )}
      </div>

      {/* Los números de apoyo. Chicos, porque no son la respuesta. */}
      {/* Los cuatro se leen como UN grupo. Repartidos en 2.200 px quedan tan
          separados que dejan de ser una fila y pasan a ser cuatro datos
          sueltos flotando. El borde superior sí toma el ancho completo,
          porque separa secciones de la tarjeta y no es texto. */}
      <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${UI.linea}` }}>
        <Row className="g-3" style={{ maxWidth: 1100 }}>
        <Pie
          titulo="Adentro ahora"
          valor={c.adentro === null ? "—" : entero(c.adentro)}
          detalle={pctOcup !== null
            ? `${pctOcup}% de tu capacidad · quedan ${entero(Math.max(0, capacidad - c.adentro))} lugares`
            : (capacidad > 0 ? `de ${entero(capacidad)} lugares` : "cárgala en Configuración › General › Operación")}
        />
        <Pie titulo="Entraron" valor={entero(c.entradasHoy)} detalle="vehículos hoy" />
        <Pie titulo="Salieron" valor={entero(c.salidasHoy)} detalle="vehículos hoy" />
        <Pie
          titulo="Se quedan"
          valor={c.permanencia ? horas(c.permanencia) : "—"}
          detalle={c.permanencia ? "lo habitual de tus últimos días" : "se calcula al actualizar"}
        />
        </Row>
      </div>
    </Bloque>
  );
};

const Pie = ({ titulo, valor, detalle }) => (
  <Col xs={6} md={3}>
    <div style={{ fontSize: 11, color: UI.textoSuave, textTransform: "uppercase", letterSpacing: 0.4 }}>
      {titulo}
    </div>
    <div style={{ fontSize: 19, fontWeight: 700, color: UI.texto, lineHeight: 1.2 }}>{valor}</div>
    <div style={{ fontSize: 11.5, color: UI.textoSuave }}>{detalle}</div>
  </Col>
);

export default PulsoDeHoy;
